import { createCraftBriefing } from './191-craft-arcade.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import {
  createSandConveyor as createPuzzle,
  collectSand,
  sandView,
  quantizeSand,
  nextSandSlot,
  sandPotCapacity,
  sandHarvest,
} from '../../../packages/domain/src/63-sand-conveyor.mjs';
import { PHOTO_SAND_DEMOS } from './57-photo-sands.mjs';
import { loadPrivatePicture, PRIVATE_PICTURE_ACCEPT } from './62-private-raster.mjs';

/** Local image-to-sand arcade. Raster handles never enter sharing or persistence.
 * Visibility-gated RAF owns presentation; the parent revokes play through allowed. */
export function createSandConveyor(
  ctx,
  { host, seed = 7, allowed = () => true, visible = () => true, onProgress = () => {} } = {},
) {
  const e = (tag, cls, text) => ctx.e(tag, cls, text);
  const frame = e('section', 'sand-arcade');
  const hud = e('div', 'sand-hud');
  const levelLabel = e('strong', '', 'SAND STUDIO'),
    score = e('strong', '', '0'),
    progress = e('span', '', 'Preparing original artwork…');
  const meter = e('progress', 'craft-progress');
  meter.max = 100;
  meter.value = 0;
  meter.setAttribute('aria-label', 'Picture gathered');
  hud.append(levelLabel, score, progress, meter);
  const canvas = e('canvas', 'sand-canvas');
  canvas.width = 480;
  canvas.height = 720;
  canvas.setAttribute('role', 'img');
  canvas.setAttribute(
    'aria-label',
    'A sixteen-colour picture made of sand. Choose a glowing colour pot below.',
  );
  const conveyor = e('div', 'sand-conveyor');
  conveyor.setAttribute('role', 'group');
  conveyor.setAttribute(
    'aria-label',
    'Six persistent collectors. Outlined station receives the next colour.',
  );
  const stations = Array.from({ length: 6 }, () => {
    const station = e('div', 'sand-moving-pot');
    conveyor.append(station);
    return station;
  });
  const palette = e('div', 'sand-palette');
  palette.setAttribute('role', 'group');
  palette.setAttribute('aria-label', 'Select a coloured collection pot');
  const feedback = e(
    'p',
    'sand-feedback',
    'Choose a glowing colour. Numbers show grains you can collect now.',
  );
  feedback.setAttribute('role', 'status');
  const controls = e('div', 'sand-actions');
  const demo = e('select');
  demo.setAttribute('aria-label', 'Original sand artwork');
  PHOTO_SAND_DEMOS.forEach((item, i) => {
    const o = e('option', '', item.label);
    o.value = String(i);
    demo.append(o);
  });
  const photo = e('button', '', 'Your photo');
  photo.type = 'button';
  paintIconAction(ctx, photo, 'image', 'Your photo', 'Your photo');
  const input = e('input');
  input.type = 'file';
  input.accept = PRIVATE_PICTURE_ACCEPT;
  input.hidden = true;
  const next = e('button', '', 'Next picture');
  next.type = 'button';
  paintIconAction(ctx, next, 'chevron-right', 'Next picture', 'Next picture');
  next.hidden = true;
  controls.append(demo, photo, next, input);
  frame.append(hud, canvas, conveyor, palette, feedback, controls);
  host.append(frame);
  const drawing = canvas.getContext('2d');
  const buttons = Array.from({ length: 16 }, (_, colour) => {
    const b = e('button', 'sand-pot');
    b.type = 'button';
    b.addEventListener('click', () => collect(colour));
    palette.append(b);
    return b;
  });
  let state,
    source,
    before,
    animation = 0,
    clock = 0,
    elapsed = 0,
    lastTick = 0,
    level = 1,
    disposed = false,
    serial = 0,
    privateHandle = null,
    previewState = null,
    harvest = [],
    bestCascade = 0,
    briefing;
  const canPlay = () => allowed() && !briefing?.isOpen();
  const reduced = () => globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  function paint() {
    demo.disabled = photo.disabled = !canPlay();
    next.disabled = !canPlay() || !state;
    if (!state) return;
    // Accepted domain state is final; presentation catches up with the grains.
    const displayed = animation && before ? before : state;
    const view = sandView(displayed);
    if (previewState !== displayed) {
      previewState = displayed;
      harvest = sandHarvest(displayed);
    }
    const largest = Math.max(...harvest);
    score.textContent = displayed.score.toLocaleString();
    frame.dataset.complete = String(displayed.complete);
    progress.textContent = `${view.percent}% gathered · ${displayed.pots} ${displayed.pots === 1 ? 'collection' : 'collections'} · ${Math.floor(elapsed / 1000)}s`;
    meter.value = view.percent;
    levelLabel.textContent = `SAND STUDIO · ${level}`;
    next.hidden = !state.complete || Boolean(animation);
    photo.hidden = !next.hidden;
    buttons.forEach((b, i) => {
      b.style.setProperty('--sand-pigment', state.palette[i]);
      const active = (animation ? state.lastPot.landed : state.collectors).some(
        (pot) => pot?.colour === i,
      );
      b.dataset.active = String(active);
      b.setAttribute('aria-pressed', String(active));
      b.dataset.exposed = String(view.exposed[i] > 0);
      b.disabled =
        disposed || !canPlay() || Boolean(animation) || state.complete || !view.exposed[i];
      b.textContent = `#${i + 1}\n${harvest[i] ? `${harvest[i]}↓` : '—'}`;
      b.dataset.best = String(harvest[i] > 0 && harvest[i] === largest);
      b.setAttribute(
        'aria-label',
        `Pigment ${i + 1}, collects ${harvest[i]} grains now${harvest[i] && harvest[i] === largest ? ', largest cascade' : ''}, ${view.remaining[i]} grains remaining${active ? ', active collector' : view.exposed[i] ? ', exposed' : ', covered'}`,
      );
    });
    paintStations();
    draw();
  }
  function paintStations() {
    const pots = animation ? state.lastPot.landed : state.collectors;
    const target = nextSandSlot(state);
    stations.forEach((station, i) => {
      const pot = pots[i];
      const departing = animation ? state.lastPot.filledSlots[i] : 0;
      station.dataset.departure = departing ? `+${departing} full` : '';
      station.dataset.empty = String(!pot);
      station.dataset.next = String(!animation && !state.complete && i === target);
      station.style.setProperty('--sand-pigment', pot ? state.palette[pot.colour] : 'transparent');
      const capacity = sandPotCapacity(state);
      station.textContent = pot
        ? `#${pot.colour + 1}\n${animation ? '↓' : `${pot.amount}/${capacity}`}`
        : state.complete && !animation
          ? '✓'
          : 'Ready';
      station.setAttribute(
        'aria-label',
        pot
          ? `Station ${i + 1}, pigment ${pot.colour + 1}, ${animation ? 'collecting' : `${pot.amount} of ${capacity} grains in current pot, ${state.filled} full pots collected overall`}${!animation && i === target ? ', next to replace' : ''}`
          : `Station ${i + 1}, ready${i === target && !state.complete ? ', next colour goes here' : ''}`,
      );
    });
  }
  function draw() {
    if (!state || !drawing) return;
    const w = canvas.width,
      h = canvas.height,
      grain = w / state.width,
      imageHeight = h - 65,
      unit = imageHeight / state.height;
    drawing.clearRect(0, 0, w, h);
    const t = animation ? Math.max(0, Math.min(1, (clock - animation) / 900)) : 1,
      ease = 1 - (1 - t) ** 3;
    for (let x = 0; x < state.width; x++) {
      const column = state.columns[x],
        old = before?.columns[x];
      column.forEach((g, y) => {
        const to = state.height - column.length + y,
          from = old ? state.height - old.length + y : to;
        drawing.fillStyle = state.palette[g.colour];
        drawing.fillRect(x * grain, (from + (to - from) * ease) * unit, grain + 0.3, unit + 0.3);
      });
    }
    if (animation) {
      for (const [i, g] of state.events.entries()) {
        const delay = (g.x / state.width) * 0.35,
          fall = Math.max(0, Math.min(1, (t - delay) / 0.65));
        const targetX = ((g.slot + 0.5) / stations.length) * w,
          x = g.x * grain + (targetX - g.x * grain) * fall * fall,
          y = g.y * unit + (imageHeight + 42 - g.y * unit) * fall;
        drawing.globalAlpha = 1 - fall * 0.4;
        drawing.fillStyle = state.palette[g.colour];
        drawing.fillRect(x + Math.sin(i * 3) * fall * 12, y, grain, unit);
      }
      drawing.globalAlpha = 1;
    }
  }
  function collect(colour) {
    if (!state || animation || disposed || !canPlay()) return;
    const candidate = collectSand(state, colour);
    if (candidate === state) return;
    before = state;
    state = candidate;
    bestCascade = Math.max(bestCascade, state.lastPot.amount);
    if (state.complete)
      state = {
        ...state,
        score: state.score + Math.max(0, 3000 - Math.floor(elapsed / 1000) * 10),
      };
    animation = reduced() ? 0 : Math.max(1, clock);
    feedback.textContent = 'Collectors working… follow the falling colours';
    frame.dataset.collecting = 'true';
    paint();
    if (!animation || !visible() || document.hidden) finishCollection();
    else controller.refresh();
  }
  function finishCollection() {
    animation = 0;
    before = null;
    frame.dataset.collecting = 'false';
    feedback.textContent = state.complete
      ? `Picture cleared! ${state.score.toLocaleString()} pts · best cascade ${bestCascade} grains.`
      : `${state.lastPot.filled > 1 ? 'POT PARTY! ' : state.lastPot.amount >= 100 ? 'BIG CASCADE! ' : ''}+${state.lastPot.amount * 10 + state.lastPot.filled * 250} · ${state.lastPot.amount} ${state.lastPot.amount === 1 ? 'grain' : 'grains'}${state.lastPot.filled ? ` · ${state.lastPot.filled} full ${state.lastPot.filled === 1 ? 'pot' : 'pots'} sent` : ' collected'}`;
    onProgress({ score: state.score, stage: level, completed: state.complete });
    paint();
  }
  async function load(url) {
    const token = ++serial;
    controller.pause();
    state = null;
    stations.forEach((station) => {
      station.textContent = 'Ready';
      station.dataset.empty = 'true';
      station.dataset.next = 'false';
    });
    next.hidden = true;
    photo.hidden = false;
    buttons.forEach((button) => {
      button.disabled = true;
    });
    feedback.textContent = 'Preparing sixteen pigments…';
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      if (disposed || token !== serial || !allowed()) return;
      const temp = document.createElement('canvas');
      temp.width = 64;
      temp.height = 88;
      const dc = temp.getContext('2d');
      if (!dc) throw new Error('Canvas unavailable');
      const scale = Math.max(64 / image.width, 88 / image.height),
        sw = 64 / scale,
        sh = 88 / scale;
      dc.drawImage(image, (image.width - sw) / 2, (image.height - sh) / 2, sw, sh, 0, 0, 64, 88);
      source = quantizeSand(dc.getImageData(0, 0, 64, 88).data, 64, 88);
      state = createPuzzle(source, level);
      before = null;
      animation = 0;
      elapsed = 0;
      bestCascade = 0;
      lastTick = 0;
      frame.dataset.complete = 'false';
      onProgress({ score: state.score, stage: level, completed: state.complete });
      feedback.textContent =
        'Numbers show grains collected now. Bright outline marks the largest cascade.';
      paint();
      controller.refresh();
    } catch {
      if (token === serial && !disposed)
        feedback.textContent =
          'This picture could not be prepared. Choose another original or photo.';
    }
  }
  demo.addEventListener('change', () => {
    if (canPlay()) {
      privateHandle?.dispose();
      privateHandle = null;
      load(PHOTO_SAND_DEMOS[Number(demo.value)].url);
    }
  });
  photo.addEventListener('click', () => {
    if (canPlay()) input.click();
  });
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    input.value = '';
    if (!file || !canPlay()) return;
    const token = ++serial;
    try {
      const handle = await loadPrivatePicture(file, {
        isCurrent: () => !disposed && token === serial && allowed(),
      });
      if (disposed || token !== serial || !canPlay()) {
        handle.dispose();
        return;
      }
      privateHandle?.dispose();
      privateHandle = handle;
      await load(handle.url);
    } catch (error) {
      if (!disposed && token === serial) feedback.textContent = error.message;
    }
  });
  next.addEventListener('click', () => {
    if (!state?.complete || !canPlay()) return;
    level = Math.min(100, level + 1);
    demo.value = String((Number(demo.value) + 1) % PHOTO_SAND_DEMOS.length);
    privateHandle?.dispose();
    privateHandle = null;
    load(PHOTO_SAND_DEMOS[Number(demo.value)].url);
  });
  demo.value = String(Math.abs(seed) % PHOTO_SAND_DEMOS.length);
  function cancel() {
    if (animation) finishCollection();
    lastTick = 0;
    frame.dataset.collecting = 'false';
    paint();
  }
  let raf = 0;
  function loop(now) {
    raf = 0;
    if (disposed || !canPlay() || !visible() || document.hidden) {
      lastTick = 0;
      return;
    }
    controller.tick(now);
    raf = requestAnimationFrame(loop);
  }
  const controller = {
    tick(now) {
      if (disposed || !canPlay() || document.hidden) {
        lastTick = 0;
        return;
      }
      const delta = lastTick ? Math.min(80, Math.max(0, now - lastTick)) : 0;
      lastTick = now;
      clock += delta;
      if (state && !state.complete) elapsed += delta;
      if (animation && clock - animation >= 900) finishCollection();
      else if (animation) draw();
      if (Math.floor(elapsed / 1000) !== Math.floor((elapsed - delta) / 1000)) paint();
    },
    refresh() {
      frame.dataset.paused = String(!visible() || !canPlay() || document.hidden);
      if (!canPlay()) cancel();
      if (disposed || !canPlay() || !visible() || document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
        lastTick = 0;
      } else if (!raf) raf = requestAnimationFrame(loop);
      return !disposed && canPlay();
    },
    cancel,
    pause() {
      cancelAnimationFrame(raf);
      raf = 0;
      cancel();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(raf);
      serial++;
      privateHandle?.dispose();
      briefing.dispose();
      frame.remove();
    },
  };
  briefing = createCraftBriefing(ctx, {
    host: frame,
    title: 'Set the colours in motion',
    steps: [
      ['① ↓', 'Tap a glowing pigment. Its exposed sand cascades into collectors.'],
      ['↓ ↓ ↓', 'Compare the numbers. Bigger cascades uncover more colours.'],
      ['▰ → ✦', 'Fill six rotating collectors, clear the picture and earn a time bonus.'],
    ],
    onOpen: () => controller.pause(),
    onClose: () => {
      paint();
      controller.refresh();
      buttons.find((button) => !button.disabled)?.focus?.({ preventScroll: true });
    },
  });
  const help = e('button', 'craft-help', 'How to play');
  help.type = 'button';
  paintIconAction(ctx, help, 'circle-help', 'How to play');
  help.addEventListener('click', () => briefing.open(help));
  controls.append(help);
  demo.disabled = photo.disabled = true;
  load(PHOTO_SAND_DEMOS[Number(demo.value)].url);
  controller.refresh();
  return controller;
}
